import { CRMRequestModal } from "../products/CreateCampaignFlow/steps/TargetAccountsStep/CRMRequestModal";
import { useState } from "react";
import { ReuseButton } from "../ui";
import { Stack, Typography } from "@mui/material";
import { ClockInCircleIcon } from "../images";
import { ResyncMarketoMenu } from "./ResyncMarketoMenu";
import {
  campaignsApi,
  useCancelIntegrationAuthMutation,
  useResendIntegrationAuthMutation,
  useSendIntegrationAuthMutation,
} from "../../api";
import { useParams } from "react-router-dom";
import { useAppDispatch } from "../../hooks";
import { toast } from "react-toastify";
import { CustomToast } from "../ui/CustomToast";
import { getErrorMessage } from "../../utils/getErrorMessage";
import { Integration } from "../../types/enums";

type Props = {
  currentEmail: string;
  marketoStatus?: string;
  isDisabled?: boolean;
  platform: Integration;
};

export const ResyncMarketoBtn = ({
  currentEmail,
  marketoStatus,
  isDisabled,
  platform,
}: Props) => {
  const { campaign_id } = useParams();
  const dispatch = useAppDispatch();

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(true);
  const [sendIntegrationAuth, { isLoading: isSendingAuth }] =
    useSendIntegrationAuthMutation();
  const [resendIntegrationAuth, { isLoading: isResendingAuth }] =
    useResendIntegrationAuthMutation();
  const [cancelIntegrationAuth, { isLoading: isCancellingAuth }] =
    useCancelIntegrationAuthMutation();

  const onSuccessHandler = () => {
    setIsOpen(false);
    setIsMenuOpen(false);
    dispatch(campaignsApi.util.invalidateTags(["summary"]));
  };

  const sendAuthHandler = (client_email: string) => {
    if (!campaign_id) return;
    sendIntegrationAuth({ client_email, campaign_id, platform })
      .unwrap()
      .then(() => {
        onSuccessHandler();
        setIsOpen(false);
      })
      .catch((err) =>
        toast.error(
          <CustomToast
            type="error"
            text={
              getErrorMessage(err).globalError ||
              "Something went wrong. Please try again"
            }
            title="Failed to Send Email"
          />,
        ),
      );
  };

  const resendAuthHandler = (client_email: string) => {
    if (!campaign_id) return;
    resendIntegrationAuth({ client_email, campaign_id, platform })
      .unwrap()
      .then(onSuccessHandler)
      .catch((err) =>
        toast.error(
          <CustomToast
            type="error"
            text={
              getErrorMessage(err).globalError ||
              "Something went wrong. Please try again"
            }
            title="Failed to Resend Email"
          />,
        ),
      );
  };

  const cancelAuthHandler = () => {
    if (!campaign_id) return;
    cancelIntegrationAuth({ campaign_id, platform })
      .unwrap()
      .then(onSuccessHandler)
      .catch((err) =>
        toast.error(
          <CustomToast
            type="error"
            text={
              getErrorMessage(err).globalError ||
              "Something went wrong. Please try again"
            }
            title="Failed to Cancel"
          />,
        ),
      );
  };

  return (
    <Stack alignItems={"flex-start"}>
      {currentEmail && marketoStatus !== "connected" ? (
        <Stack flexDirection={"row"} alignItems={"center"} gap={"4px"}>
          <Typography variant="button" sx={{ color: "#464646" }}>
            Pending
          </Typography>
          <ClockInCircleIcon />
          <ResyncMarketoMenu
            isMenuOpen={isMenuOpen}
            isLoading={isResendingAuth || isCancellingAuth}
            currentEmail={currentEmail}
            resendHandler={resendAuthHandler}
            cancelHandler={cancelAuthHandler}
          />
        </Stack>
      ) : (
        <ReuseButton
          height={"32px"}
          onClick={() => setIsOpen(true)}
          disabled={isDisabled}
        >
          Credentials Request
        </ReuseButton>
      )}

      {(isOpen || isSendingAuth) && (
        <CRMRequestModal
          currentEmail={currentEmail}
          setCurrentEmail={sendAuthHandler}
          onClose={() => setIsOpen(false)}
          isLoading={isSendingAuth}
          modalType={"send"}
          crmType={platform}
        />
      )}
    </Stack>
  );
};
